<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useOrgStore } from '../stores/orgStore.js'
import { useLotteryStore } from '../stores/lottery.js'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import { isDataStale, paginateRows, toPositiveInt } from '../utils/operationsUi.js'
import {
  downloadCsv,
  getRecentExportHistory,
  recordExportEvent,
  toCsv,
  toLifecycleFilename,
  toLotteryReconciliationRows,
  toOrgFinancialSummaryRows,
} from '../utils/exportUi.js'
import {
  getReconciliationIndicators,
  summarizeLotterySales,
  summarizeOrgSales,
  summarizeSubOrgSales,
} from '../utils/financialUi.js'
import { useQueryState } from '../composables/useQueryState.js'
import { useFinancialSummary } from '../composables/useFinancialSummary.js'
import ReconciliationBadge from '../components/ReconciliationBadge.vue'
import IntegrityWarningPanel from '../components/IntegrityWarningPanel.vue'
import FinancialSummaryCards from '../components/FinancialSummaryCards.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const orgStore = useOrgStore()
const lotteryStore = useLotteryStore()

const loading = ref(false)
const error = ref('')
const degradedWarning = ref('')
const lastSuccessfulRefreshAt = ref('')
const lifecycleFilter = ref('active')
const page = ref(1)
const pageSize = ref(25)
const exportDialogOpen = ref(false)
const exportLoading = ref(false)
const exportError = ref('')
const exportScope = ref('org-financial-summary')
const exportHistory = ref([])

const staleData = computed(() => isDataStale(lastSuccessfulRefreshAt.value, 120000))

const queryState = useQueryState({
  route,
  router,
  entries: [
    { queryKey: 'lifecycle', defaultValue: 'active', get: () => lifecycleFilter.value, set: (value) => { lifecycleFilter.value = String(value || 'active') } },
    { queryKey: 'page', type: 'int', defaultValue: 1, get: () => page.value, set: (value) => { page.value = toPositiveInt(value, 1) } },
    { queryKey: 'page_size', type: 'int', defaultValue: 25, get: () => pageSize.value, set: (value) => { pageSize.value = toPositiveInt(value, 25) } },
  ],
})

const syncQueryFromState = async () => {
  await queryState.syncToRoute()
}

const hydrateStateFromQuery = () => {
  queryState.hydrateFromQuery()
}

const organizationsById = computed(() => {
  return new Map((orgStore.organizations || []).map((org) => [Number(org?.id), org]))
})

const normalizedLotteries = computed(() => {
  return (lotteryStore.lotteries || []).map((lottery) => summarizeLotterySales(lottery, {
    ticketPrice: lottery?.ticket_price ?? 0,
  }))
})

const lifecycleFilteredLotteries = computed(() => {
  if (lifecycleFilter.value === 'all') return normalizedLotteries.value

  return normalizedLotteries.value.filter((row) => {
    const org = organizationsById.value.get(Number(row.orgId))
    if (!org) return lifecycleFilter.value !== 'active'

    if (lifecycleFilter.value === 'deleted') return org.is_deleted === true
    return org.is_deleted !== true
  })
})

const financialSummary = useFinancialSummary({
  lotteries: lifecycleFilteredLotteries,
  lifecycleFilter,
})

const paginatedLotteries = computed(() => paginateRows(lifecycleFilteredLotteries.value, page.value, pageSize.value))
const visibleLotteries = computed(() => paginatedLotteries.value.items)
const orgSummaryRows = computed(() => summarizeOrgSales(lifecycleFilteredLotteries.value))
const subOrgSummaryRows = computed(() => summarizeSubOrgSales(lifecycleFilteredLotteries.value))

const totals = computed(() => {
  return lifecycleFilteredLotteries.value.reduce((acc, row) => {
    acc.totalTickets += Number(row.totalTickets)
    acc.soldTickets += Number(row.soldTickets)
    acc.reservedTickets += Number(row.reservedTickets)
    acc.refundedTickets += Number(row.refundedTickets)
    acc.voidedTickets += Number(row.voidedTickets)
    acc.estimatedRevenue += Number(row.estimatedRevenue)
    acc.mismatchCount += Number(row.mismatchCount)
    acc.partial = acc.partial || row.partial
    acc.degraded = acc.degraded || row.degraded
    return acc
  }, {
    totalTickets: 0,
    soldTickets: 0,
    reservedTickets: 0,
    refundedTickets: 0,
    voidedTickets: 0,
    estimatedRevenue: 0,
    mismatchCount: 0,
    partial: false,
    degraded: false,
  })
})

const summaryIndicator = computed(() => getReconciliationIndicators(totals.value))
const summaryCards = computed(() => ([
  { key: 'total', label: 'Total tickets', value: totals.value.totalTickets, tone: 'info' },
  { key: 'sold', label: 'Sold tickets', value: totals.value.soldTickets, tone: 'success' },
  { key: 'reserved', label: 'Reserved tickets', value: totals.value.reservedTickets, tone: 'warning' },
  { key: 'refunded-voided', label: 'Refunded / Voided', value: `${totals.value.refundedTickets} / ${totals.value.voidedTickets}`, tone: 'warning' },
  { key: 'revenue', label: 'Estimated revenue', value: totals.value.estimatedRevenue, tone: 'info' },
]))

const refresh = async () => {
  loading.value = true
  error.value = ''
  degradedWarning.value = ''
  try {
    await Promise.all([
      orgStore.fetchMyOrganizations(),
      lotteryStore.fetchLotteries(),
    ])

    if (lifecycleFilteredLotteries.value.some((row) => row.partial || row.degraded)) {
      degradedWarning.value = 'Financial overview includes partial/degraded datasets. Reconciliation should be re-checked.'
    }

    lastSuccessfulRefreshAt.value = new Date().toISOString()
    exportHistory.value = getRecentExportHistory()
  } catch {
    error.value = 'Failed to refresh financial overview.'
  } finally {
    loading.value = false
  }
}

const openExportDialog = (scope) => {
  exportScope.value = scope
  exportError.value = ''
  exportDialogOpen.value = true
}

const closeExportDialog = () => {
  if (exportLoading.value) return
  exportDialogOpen.value = false
}

const exportRows = computed(() => {
  if (exportScope.value === 'lottery-reconciliation') return toLotteryReconciliationRows(lifecycleFilteredLotteries.value)
  return toOrgFinancialSummaryRows(orgSummaryRows.value)
})

const exportFinancialCsv = async () => {
  if (!exportRows.value.length || exportLoading.value) return
  exportLoading.value = true
  exportError.value = ''
  try {
    const headers = Object.keys(exportRows.value[0] || {}).map((key) => ({ key, label: key }))
    const csv = toCsv(exportRows.value, headers)
    const filename = toLifecycleFilename({
      scope: exportScope.value,
      lifecycle: lifecycleFilter.value,
    })

    downloadCsv(filename, csv)
    recordExportEvent({
      exported_by: authStore.user?.username || authStore.user?.email || 'unknown',
      export_scope: exportScope.value,
      partial_export_warning: totals.value.partial || totals.value.degraded,
      record_count: exportRows.value.length,
    })
    exportHistory.value = getRecentExportHistory()
    exportDialogOpen.value = false
  } catch {
    exportError.value = 'Failed to export financial summary.'
  } finally {
    exportLoading.value = false
  }
}

watch([lifecycleFilter, pageSize], async () => {
  page.value = 1
  await syncQueryFromState()
})

watch(page, async () => {
  await syncQueryFromState()
})

onMounted(async () => {
  hydrateStateFromQuery()
  await refresh()
  await syncQueryFromState()
})
</script>

<template>
  <section class="financial-overview-view">
    <header class="header-row">
      <div>
        <h1>Financial Overview</h1>
        <p class="muted">Read-only operational sales and reconciliation overview.</p>
        <p v-if="lastSuccessfulRefreshAt" class="muted">Last successful refresh: {{ lastSuccessfulRefreshAt }}</p>
      </div>
      <div class="actions">
        <button class="btn" type="button" :disabled="loading" @click="refresh">{{ loading ? 'Refreshing...' : 'Refresh' }}</button>
        <button class="btn" type="button" :disabled="loading || !orgSummaryRows.length" @click="openExportDialog('org-financial-summary')">Export org summary</button>
        <button class="btn" type="button" :disabled="loading || !lifecycleFilteredLotteries.length" @click="openExportDialog('lottery-reconciliation')">Export reconciliation</button>
      </div>
    </header>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="degradedWarning" class="warning">{{ degradedWarning }}</p>
    <p v-if="staleData && !loading" class="warning">Overview may be stale. Refresh before operational decisions.</p>

    <div class="summary-sticky">
      <FinancialSummaryCards :cards="summaryCards" />
      <article class="summary-card" :class="{ warning: summaryIndicator.hasMismatch || summaryIndicator.partial || summaryIndicator.degraded }">
        <h2>Reconciliation</h2>
        <ReconciliationBadge :indicator="summaryIndicator" source="financial_overview" />
      </article>
    </div>

    <IntegrityWarningPanel :warnings="financialSummary.warningSignals || []" title="Operational Warning Signals" />

    <div class="panel controls">
      <label>
        Lifecycle
        <select v-model="lifecycleFilter" class="form-select">
          <option value="all">All organizations</option>
          <option value="active">Active organizations</option>
          <option value="deleted">Deleted organizations</option>
        </select>
      </label>
      <label>
        Page size
        <select v-model="pageSize" class="form-select">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </label>
      <p class="muted">Pagination is query-persisted for operational continuity.</p>
    </div>

    <section class="panel">
      <h2>Lottery reconciliation rows</h2>
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th>Lottery</th>
              <th>Total</th>
              <th>Sold</th>
              <th>Reserved</th>
              <th>Refunded</th>
              <th>Voided</th>
              <th>Revenue</th>
              <th>Warnings</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleLotteries" :key="`lottery-fin-${row.lotteryId}`">
              <td>#{{ row.lotteryId }} {{ row.name || 'Unnamed' }}</td>
              <td>{{ row.totalTickets }}</td>
              <td>{{ row.soldTickets }}</td>
              <td>{{ row.reservedTickets }}</td>
              <td>{{ row.refundedTickets }}</td>
              <td>{{ row.voidedTickets }}</td>
              <td>{{ row.estimatedRevenue }}</td>
              <td>{{ row.warnings.length ? row.warnings.join(' ') : 'OK' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <footer v-if="lifecycleFilteredLotteries.length" class="pagination-row">
        <button class="btn" type="button" :disabled="page <= 1" @click="page -= 1">Previous</button>
        <span class="muted">Page {{ paginatedLotteries.page }} of {{ paginatedLotteries.totalPages }} ({{ paginatedLotteries.total }} rows)</span>
        <button class="btn" type="button" :disabled="page >= paginatedLotteries.totalPages" @click="page += 1">Next</button>
      </footer>
    </section>

    <section class="panel">
      <h2>Per organization totals</h2>
      <div class="mobile-card-grid">
        <article class="mobile-card" v-for="row in orgSummaryRows" :key="`org-summary-${row.orgId || 'unscoped'}`">
          <h3>Org #{{ row.orgId || 'unscoped' }}</h3>
          <p>Total tickets: {{ row.totalTickets }}</p>
          <p>Sold / Reserved: {{ row.soldTickets }} / {{ row.reservedTickets }}</p>
          <p>Estimated revenue: {{ row.estimatedRevenue }}</p>
          <p>Mismatch count: {{ row.mismatchCount }}</p>
        </article>
      </div>
    </section>

    <section class="panel">
      <h2>Per sub-organization totals</h2>
      <div class="mobile-card-grid">
        <article class="mobile-card" v-for="row in subOrgSummaryRows" :key="`suborg-summary-${row.subOrgId || 'unscoped'}`">
          <h3>Sub-org #{{ row.subOrgId || 'unscoped' }}</h3>
          <p>Total tickets: {{ row.totalTickets }}</p>
          <p>Sold / Reserved: {{ row.soldTickets }} / {{ row.reservedTickets }}</p>
          <p>Estimated revenue: {{ row.estimatedRevenue }}</p>
          <p>Mismatch count: {{ row.mismatchCount }}</p>
        </article>
      </div>
    </section>

    <section class="panel">
      <h2>Recent Exports</h2>
      <ul v-if="exportHistory.length" class="list">
        <li v-for="entry in exportHistory" :key="entry.id">
          {{ entry.export_scope }} - {{ entry.record_count }} rows - {{ entry.exported_at }}
          <span v-if="entry.partial_export_warning">(partial warning)</span>
        </li>
      </ul>
      <p v-else class="muted">No exports recorded yet.</p>
    </section>

    <AdminActionDialog
      :open="exportDialogOpen"
      title="Export Financial Summary"
      :message="`Export scope: ${exportScope}`"
      :details="[
        exportRows.length ? `Rows: ${exportRows.length}` : 'No rows available.',
        'CSV-only export for audit and administrative workflows.',
      ]"
      tone="warning"
      confirm-label="Export CSV"
      :loading="exportLoading"
      loading-label="Preparing export..."
      @close="closeExportDialog"
      @confirm="exportFinancialCsv"
    />

    <p v-if="exportError" class="error">{{ exportError }}</p>
  </section>
</template>

<style scoped>
.financial-overview-view { max-width: 1200px; margin: 0 auto; padding: 2rem; display: grid; gap: 0.85rem; }
.header-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
.actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.panel { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 0.75rem; }
.controls { display: flex; gap: 0.75rem; align-items: end; flex-wrap: wrap; }
.summary-sticky { position: sticky; top: 58px; z-index: 2; display: grid; gap: 0.65rem; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
.summary-card { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 0.65rem; }
.table-wrap { overflow-x: auto; }
.table { width: 100%; border-collapse: collapse; }
.table th, .table td { border-bottom: 1px solid #e5e7eb; padding: 0.5rem; text-align: left; }
.mobile-card-grid { display: grid; gap: 0.6rem; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
.mobile-card { border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb; padding: 0.65rem; }
.pagination-row { margin-top: 0.5rem; display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; }
.form-select { border: 1px solid #d1d5db; border-radius: 6px; min-height: 40px; padding: 0.4rem; }
.btn { border: none; border-radius: 6px; background: #2563eb; color: #fff; padding: 0.45rem 0.75rem; }
.warning { color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 0.45rem; }
.error { color: #991b1b; background: #fee2e2; border-radius: 6px; padding: 0.45rem; }
.muted { color: #6b7280; }
.list { margin: 0; padding-left: 1rem; color: #374151; }

@media (max-width: 768px) {
  .summary-sticky { position: sticky; bottom: 0; top: auto; }
  .actions .btn { min-height: 44px; }
}
</style>

